import { PALETTE, SYSTEM_USERNAMES, entityLabel, periodAdjective, periodLabel } from './activityReport'

// Re-exported so callers that think of this as "the registry's" list can
// still import it from here. Defined in `activityReport.js`, not here -- see
// the comment on `SYSTEM_USERNAMES` there for why importing it in the other
// direction is a circular import that breaks at module-init.
export { SYSTEM_USERNAMES }

/**
 * Every report the dashboard renders. Mirrors `activity/registry.py`; the ids
 * and action names must match it exactly, because the server sends rows keyed by
 * those action names.
 *
 * Adding a report is one entry here plus one in the Python registry. Nothing
 * else -- no new query, hook, component or chart code.
 *
 * `metric` mirrors the two count columns the server returns: creations count
 * distinct objects, updates count history events (several fire per object).
 *
 * Colours come from the validated 8-slot palette and are assigned per report, so
 * within one report a colour always means the same action. They are not shared
 * across reports; two reports are never on screen at once.
 */

const created = (value, label, entity, color) => ({
    value,
    label,
    entity,
    color,
    mode: 'created',
    metric: 'objectsCount',
})

const updated = (value, label, entity, color) => ({
    value,
    label,
    entity,
    color,
    mode: 'updated',
    metric: 'eventsCount',
})

// The chart layout every activity report uses. Declared once and shared, so the
// reports stay visually consistent and a change lands everywhere at once. A
// report that needs something different overrides `charts` in its own entry.
const STANDARD_CHARTS = [
    { type: 'trend', title: '{Period} activity', caption: 'Events per {period} across every action in this report' },
    { type: 'actionMix', groupBy: 'userName', title: 'By staff member', caption: 'Created vs edited, this range' },
    { type: 'actionMix', groupBy: 'branchName', title: 'By branch', caption: 'Created vs edited, this range' },
    { type: 'heatmap', title: 'Who worked on this, by {period}', caption: 'Darker cells mean more events that {period}' },
]

// The Users view every report shares: who did the work, not what it was on.
// Declared once, exactly like STANDARD_CHARTS, because the whole point of this
// view is that it is the same generic three-chart layout on every report --
// nothing here ever needs a report-specific override. Built from the report's
// own actions (via `actionMeta` / `chart.actions` fallback in ReportBody), so
// it is naturally scoped to that report's own activity, never another one's.
const USER_CHARTS = [
    { type: 'leaderboard', title: 'Effort by user', caption: 'Two bars per person -- created above, updated below' },
    {
        type: 'trend',
        title: 'Activity over time',
        caption: '{Period} totals by record type, this range -- coloured to match the leaderboard above',
        // Coloured by entity, not action, so this shares its colour axis with
        // the leaderboard above it rather than inventing a second one. An
        // unscoped action-keyed trend needs one colour per action -- for
        // `incidents` that is 10 series over an 8-slot palette, which both
        // collides two unrelated actions onto the same colour and disagrees
        // with the leaderboard about what `PALETTE[0]` means on this tab. See
        // `seriesForChart` / `entityPaletteFor` in this file.
        colorBy: 'entity',
    },
    { type: 'heatmap', title: '{Period} intensity', caption: 'Darker cells mean more events that {period}' },
]

/**
 * The By-<scope> tab: the By Users tab's three charts, keyed by the report's
 * scope dimension (board, inbox) instead of the person. Titles are completed
 * per report by `scopeChartsFor`. The chart types are distinct from the
 * Users-view ones so `ReportBody` can key them off `row.scopeNames` without
 * inspecting `layout` inside the chart loop.
 *
 * A known, accepted trade-off lives on this tab: the scope trend
 * (`scopeTrend`) colours its lines by board -- `PALETTE[index]` in ranked-
 * volume order, from `buildScopeTrend` -- while the scope leaderboard
 * (`scopeLeaderboard`) colours its bars by entity -- `PALETTE[index]` in
 * `entityPaletteFor`'s order, from `buildLeaderboard`. So `PALETTE[0]` means
 * two different things on the same tab: the busiest board in one chart, Task
 * (say) in the other. Every other tab in this file keeps one colour axis per
 * tab specifically to avoid this; a by-board trend cannot, because its whole
 * point is one line per board, and boards are not a fixed, report-declared
 * set the way entities are -- there is no "entity palette" for something
 * this data-dependent to borrow instead. Leave both charts colouring what
 * they each already do; re-keying either one to make them agree would just
 * break the axis that chart actually needs.
 */
const SCOPE_CHARTS = [
    { type: 'scopeLeaderboard', title: 'Effort by {scope}', caption: 'Two bars per {scope} -- created above, updated below' },
    { type: 'scopeTrend', title: 'Activity over time by {scope}', caption: '{Period} totals for the busiest {scope}s, the rest folded into Other' },
    { type: 'scopeHeatmap', title: '{Period} intensity by {scope}', caption: 'Darker cells mean more events that {period}' },
]

/**
 * Fills a chart's `{Period}`/`{period}` tokens for the current granularity --
 * `{Period}` an adjective ("Daily"/"Weekly"/"Monthly"), `{period}` the bare
 * noun ("day"/"week"/"month"), from `periodAdjective`/`periodLabel` in
 * activityReport.js. Applied by `ReportBody` to `chart.title`/`chart.caption`
 * right before they reach `ChartCard`, so the registry itself stays
 * granularity-agnostic -- the same object serves every switch position, this
 * just reads it differently on each render. Independent of the `{scope}` fill
 * `scopeChartsFor` does above: that one runs once, when a report's scope
 * charts are built, and only ever touches `{scope}`/`{scope}s`, leaving any
 * `{Period}`/`{period}` token in the same string untouched for this to fill
 * in later.
 */
export const fillPeriod = (text, granularity) =>
    text.replace(/\{Period\}/g, periodAdjective(granularity)).replace(/\{period\}/g, periodLabel(granularity))

export const scopeTabFor = (report) => {
    if (!report.scopeTab) return null
    if (!report.scopeLabel) throw new Error(`${report.id} declares scopeTab without a scopeLabel`)
    return { id: 'scope', name: `By ${report.scopeLabel}`, label: report.scopeLabel, plural: report.scopePlural || `${report.scopeLabel}s` }
}

export const scopeChartsFor = (report) => {
    const tab = scopeTabFor(report)
    if (!tab) return []
    const fill = (text) => text.replace(/\{scope\}s/g, tab.plural.toLowerCase()).replace(/\{scope\}/g, tab.label.toLowerCase())
    return SCOPE_CHARTS.map((chart) => ({ ...chart, title: fill(chart.title), caption: fill(chart.caption) }))
}

// Index of the by-branch chart within STANDARD_CHARTS -- the insertion point
// `standardChartsFor` (below) uses for a report's "by <dimension>" chart, so
// that chart always lands where the by-branch chart itself would sit.
const BRANCH_CHART_INDEX = STANDARD_CHARTS.findIndex(
    (chart) => chart.type === 'actionMix' && chart.groupBy === 'branchName'
)

/**
 * The two series a `colorBy: 'direction'` chart resolves to. Distinct palette
 * slots, because on a stacked bar colour is the only thing separating two
 * touching segments -- and slots `inbox.actions` does not already use, since
 * the by-staff chart these colour (see `colorStaffByDirection` below) sits
 * beside `inbox`'s by-branch and by-inbox charts, which still colour by
 * action: reusing PALETTE[0]/PALETTE[1] would make the same blue mean
 * "Message created" (an action that fires for inbound as well as outbound
 * messages) on one card and "Inbound" on the card beside it.
 */
const DIRECTION_SERIES = [
    { key: 'inbound', label: 'Inbound', color: PALETTE[4] },
    { key: 'outbound', label: 'Outbound', color: PALETTE[5] },
]

/**
 * The standard module-view chart layout for a report, adjusted by its
 * `includeBranchChart` and `scopeChartLabel` params -- each call site below
 * sets these from its own report's `hasBranchAttribution` and `scopeLabel`
 * properties, the same two independent switches `activity/registry.py`'s
 * `ReportSpec` declares. Driven entirely off those two values rather than a
 * report id, so a future report with either flag set gets the right layout
 * without a new branch here.
 *
 * Named differently from the report properties they are sourced from, on
 * purpose: `activity/tests/test_frontend_contract.py` pins each report's
 * top-level `scopeLabel`/`hasBranchAttribution` against the Python backend
 * by regexing the literal property line out of this file, anchored to the
 * start of a line so it cannot match an argument passed on the same line to
 * a function call. A parameter here sharing one of those names -- as
 * `hasDirection` used to, see `colorStaffByDirection` -- would only stay
 * invisible to that anchor by accident of formatting (both still fit on the
 * `charts:` line below); reflowing either call site across multiple lines
 * would silently recreate the same false pin. See the comment above
 * `_parse_js_scope_labels` / `_parse_js_has_branch_attribution` in that test
 * file for the other half of this.
 *
 * `includeBranchChart: false` drops the by-branch chart (currently only
 * `boards` -- `task_rabbit.Board` has no branch, tenant or org link at all,
 * so offering it would just show every row bucketed under
 * `NOT_BRANCH_ATTRIBUTED`, one meaningless bar). Filtering `STANDARD_CHARTS`
 * by `groupBy` rather than hand-writing a shorter list keeps this in sync
 * with it automatically -- a future change to the shared trend, staff or
 * heatmap chart still reaches every report built this way.
 *
 * `scopeChartLabel`, when set, adds a "By <label>" `actionMix` chart grouped by
 * `scopeNames` (the one generic field every report's rows carry for this --
 * see `activity/schema_nodes.py`'s `ActivityReportRowNode.scope_names`), inserted
 * at the by-branch chart's own position: right after it when branch
 * attribution is also on (`inbox` ends up with both a by-branch and a
 * by-inbox chart), or in its place when there is no branch chart to sit next
 * to (`boards` ends up with just the one, by-board, chart). Phrasing matches
 * the by-branch chart's own caption ("Created vs edited, this range") and
 * title casing ("By branch" -> "By board" / "By inbox").
 *
 * `scopeNames` is a *list* -- see `ActivityReportRowNode.scope_names`'s own
 * docstring -- so this chart is where an object with several values (a
 * message routed into several inboxes) is deliberately double counted, once
 * per value: `buildActionMix` (activityReport.js) contributes a row's count
 * to every group named in its `groupKeyFn` result when that result is an
 * array, which is exactly what `row.scopeNames` is here. Every other chart
 * in this file groups by a scalar field (`userName`, `branchName`) and so
 * still counts each row once -- nothing about that changes; the double
 * counting is confined to this one chart (and, in `activityReport.js`, the
 * scope filter's any-match) by what the field actually holds, not by a
 * special case written for it.
 *
 * `selfScopedChartsFor` (below) already degrades gracefully when a report's
 * `charts` has no branchName-grouped chart to find, so nothing else needs to
 * know about either switch.
 */
const standardChartsFor = ({ includeBranchChart = true, scopeChartLabel = null, colorStaffByDirection = false } = {}) => {
    let charts = includeBranchChart
        ? [...STANDARD_CHARTS]
        : STANDARD_CHARTS.filter((chart) => !(chart.type === 'actionMix' && chart.groupBy === 'branchName'))

    if (scopeChartLabel) {
        const scopeChart = {
            type: 'actionMix',
            groupBy: 'scopeNames',
            title: `By ${scopeChartLabel.toLowerCase()}`,
            caption: 'Created vs edited, this range',
        }
        const insertAt = includeBranchChart ? BRANCH_CHART_INDEX + 1 : BRANCH_CHART_INDEX
        charts = [...charts.slice(0, insertAt), scopeChart, ...charts.slice(insertAt)]
    }

    // On a report with a direction dimension the by-staff chart answers
    // "inbound or outbound work?" rather than "created or edited?". Keeping
    // both would put four segments in every bar, which is where a stacked bar
    // stops being readable.
    //
    // Note what this chart deliberately does *not* do: show the inbound
    // messages nobody on staff touched. Those arrive by webhook with no actor,
    // so they belong to the "No Actor" row, which the default Role filter
    // (Reps only -- see `ROLE_OPTIONS`/`DEFAULT_FILTERS` in ReportsPanel.js)
    // does not admit -- selecting the System role makes that bar appear here
    // on its own, through the ordinary filter, with no special case in this
    // file. The in/out totals in the stat tile beside it (see
    // `buildDirectionTotals` in activityReport.js) go through that same
    // filter, so a No-Actor row's inbound work stays invisible there too
    // until System is selected.
    if (colorStaffByDirection) {
        charts = charts.map((chart) =>
            chart.type === 'actionMix' && chart.groupBy === 'userName'
                ? { ...chart, colorBy: 'direction', caption: 'Inbound vs outbound, this range' }
                : chart
        )
    }

    return charts
}

export const REPORTS = {
    incidents: {
        id: 'incidents',
        name: 'Incidents',
        subtitle: 'Incidents, parties and documents opened and edited, counted from the history log — not live totals.',
        // The "Incidents created"/"Incidents edited" stat tiles must count the
        // incident itself, not its parties or documents -- otherwise
        // "Incidents created" silently includes a document upload, which is
        // not an incident. See `headlineActions` below, which scopes those
        // two tiles down to just the incident actions; every other action
        // still counts toward "Total edit events" (see `buildKpis`).
        noun: 'Incidents',
        headlineActions: ['INCIDENT_CREATED', 'INCIDENT_UPDATED'],
        actions: [
            created('INCIDENT_CREATED', 'Incident opened', 'INCIDENT', PALETTE[0]),
            updated('INCIDENT_UPDATED', 'Incident edited', 'INCIDENT', PALETTE[1]),
            created('PARTY_CREATED', 'Party added', 'PARTY', PALETTE[2]),
            updated('PARTY_UPDATED', 'Party edited', 'PARTY', PALETTE[3]),
            created('PARTY_PEOPLE_CREATED', 'Party person added', 'PARTY_PEOPLE', PALETTE[4]),
            updated('PARTY_PEOPLE_UPDATED', 'Party person edited', 'PARTY_PEOPLE', PALETTE[5]),
            created('PARTY_VEHICLE_CREATED', 'Party vehicle added', 'PARTY_VEHICLE', PALETTE[6]),
            updated('PARTY_VEHICLE_UPDATED', 'Party vehicle edited', 'PARTY_VEHICLE', PALETTE[7]),
            created('DOCUMENT_UPLOADED', 'Document uploaded', 'DOCUMENT', PALETTE[0]),
            updated('DOCUMENT_UPDATED', 'Document edited', 'DOCUMENT', PALETTE[1]),
        ],
        // 10 actions is more than the 8-slot palette can give distinct
        // colours, but that only matters where colour is the sole
        // differentiator -- a chart legend, or two touching bar segments.
        // This report's table (`DataTable`) is not that: every column
        // already carries its own text header ("Incident opened" vs
        // "Document uploaded"), and the value cell's colour chip is a
        // decorative highlight, not what tells columns apart. `PALETTE`'s
        // own comment says as much -- the relief rule is that every chart
        // using low-contrast slots ships a table view, i.e. the table is
        // precisely the place viewers are not meant to rely on colour. So the
        // table renders one column per action here, same as `insurance` (6
        // actions, also uncollapsed) already does.
        //
        // The daily trend below is the one chart that does combine every
        // entity: the user asked for all five record types, both created
        // and updated, on one line chart -- ten series over an eight-slot
        // palette. It solves that by colouring per entity (`colorBy:
        // 'entityPerAction'`, five hues, matching the leaderboard) and
        // carrying created-vs-updated as line style instead of a second
        // colour -- solid for created, dashed for updated. See
        // `seriesForChart` for how that is derived, and `TrendLine` for how
        // the dashing renders. An incident is a case and a document is a
        // file attached to one, so this still mixes units by count; the
        // chart's caption says so rather than implying they are
        // comparable.
        //
        // Every other chart below declares its own `actions`, scoped to one
        // entity (incidents, parties, or documents), so no single one of
        // them ever mixes more than two colours under a per-action legend,
        // and per-action detail (e.g. "Incident opened" vs "Document
        // uploaded") survives instead of being flattened to generic
        // created/edited labels. The party actions (party, party person,
        // party vehicle x created/updated) get their own chart here too --
        // those six actions map to `PALETTE[2]`-`PALETTE[7]`, all distinct,
        // so a per-action legend is safe there as well, consistent with the
        // incidents and documents charts either side of it.
        charts: [
            {
                type: 'trend',
                title: '{Period} incident activity',
                caption: 'Every record type this report tracks, per {period} -- solid lines are new records, dashed are edits, colour matches the leaderboard. Mixes cases, parties and documents, so read this as overall activity volume, not an incident count.',
                colorBy: 'entityPerAction',
                actions: [
                    'INCIDENT_CREATED',
                    'INCIDENT_UPDATED',
                    'PARTY_CREATED',
                    'PARTY_UPDATED',
                    'PARTY_PEOPLE_CREATED',
                    'PARTY_PEOPLE_UPDATED',
                    'PARTY_VEHICLE_CREATED',
                    'PARTY_VEHICLE_UPDATED',
                    'DOCUMENT_UPLOADED',
                    'DOCUMENT_UPDATED',
                ],
            },
            {
                type: 'actionMix',
                groupBy: 'userName',
                title: 'Incidents by staff member',
                caption: 'Opened vs edited, this range',
                actions: ['INCIDENT_CREATED', 'INCIDENT_UPDATED'],
            },
            {
                type: 'actionMix',
                groupBy: 'branchName',
                title: 'Incidents by branch',
                caption: 'Opened vs edited, this range',
                actions: ['INCIDENT_CREATED', 'INCIDENT_UPDATED'],
            },
            {
                type: 'actionMix',
                groupBy: 'userName',
                title: 'Documents by staff member',
                caption: 'Uploaded vs edited, this range -- kept separate from incidents: a document is a file attached to a case, not a comparable unit',
                actions: ['DOCUMENT_UPLOADED', 'DOCUMENT_UPDATED'],
            },
            {
                type: 'actionMix',
                groupBy: 'userName',
                title: 'Parties by staff member',
                caption: 'Parties, party people and party vehicles -- added vs edited, this range',
                actions: [
                    'PARTY_CREATED',
                    'PARTY_UPDATED',
                    'PARTY_PEOPLE_CREATED',
                    'PARTY_PEOPLE_UPDATED',
                    'PARTY_VEHICLE_CREATED',
                    'PARTY_VEHICLE_UPDATED',
                ],
            },
            { type: 'heatmap', title: 'Who worked on this, by {period}', caption: 'Darker cells mean more events that {period}' },
        ],
        userCharts: USER_CHARTS,
        // Documents no longer fold into "Incidents created" (see
        // `headlineActions` above), so they get their own tile instead of
        // disappearing from the headline row entirely. Counts distinct
        // documents, not upload events: DOCUMENT_UPLOADED is a created-mode
        // action, so it already reads `objectsCount` -- the same distinct-object
        // metric "Incidents created" reads -- rather than `eventsCount`, which
        // is what "uploaded" should mean here (how many documents landed in
        // this range, not how many upload-related events fired). See
        // `buildExtraTileValue` in activityReport.js.
        extraTiles: [
            {
                key: 'documents',
                label: 'Documents uploaded',
                caption: 'distinct documents added in this range',
                actions: ['DOCUMENT_UPLOADED'],
                testId: 'kpi-documents-uploaded',
            },
        ],
    },
    claims: {
        id: 'claims',
        name: 'Claims',
        subtitle: 'Liability and subrogation claims raised and worked, counted from the history log — not live totals.',
        // Both liability and subrogation claims are claims, so all four
        // actions are headline -- no `headlineActions` override needed, the
        // default (every action in the report) already reads right here.
        noun: 'Claims',
        actions: [
            created('LIABILITY_CLAIM_CREATED', 'Liability claim opened', 'LIABILITY_CLAIM', PALETTE[0]),
            updated('LIABILITY_CLAIM_UPDATED', 'Liability claim edited', 'LIABILITY_CLAIM', PALETTE[1]),
            created('SUBROGATION_CLAIM_CREATED', 'Subro claim opened', 'SUBROGATION_CLAIM', PALETTE[2]),
            updated('SUBROGATION_CLAIM_UPDATED', 'Subro claim edited', 'SUBROGATION_CLAIM', PALETTE[3]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    reservations: {
        id: 'reservations',
        name: 'Reservations',
        subtitle: 'Reservations created, amended and cancelled, counted from the history log — not live totals.',
        noun: 'Reservations',
        actions: [
            created('RESERVATION_CREATED', 'Reservation created', 'RESERVATION', PALETTE[0]),
            updated('RESERVATION_UPDATED', 'Reservation edited', 'RESERVATION', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    agreements: {
        id: 'agreements',
        name: 'Agreements',
        subtitle: 'Rental agreements opened, edited and closed, counted from the history log — not live totals.',
        noun: 'Agreements',
        actions: [
            created('AGREEMENT_CREATED', 'Agreement opened', 'AGREEMENT', PALETTE[0]),
            updated('AGREEMENT_UPDATED', 'Agreement edited', 'AGREEMENT', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    car_returns: {
        id: 'car_returns',
        name: 'Car Returns',
        subtitle: 'Return requests raised, confirmed and closed out, counted from the history log — not live totals.',
        noun: 'Car returns',
        actions: [
            created('CAR_RETURN_CREATED', 'Return created', 'CAR_RETURN', PALETTE[0]),
            updated('CAR_RETURN_UPDATED', 'Return edited', 'CAR_RETURN', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    payment_plans: {
        id: 'payment_plans',
        name: 'Payment Plans',
        subtitle: 'Paydown plans set up, adjusted and deactivated, counted from the history log — not live totals.',
        noun: 'Payment plans',
        actions: [
            created('PAYMENT_PLAN_CREATED', 'Plan created', 'PAYMENT_PLAN', PALETTE[0]),
            updated('PAYMENT_PLAN_UPDATED', 'Plan edited', 'PAYMENT_PLAN', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    scheduled_payments: {
        id: 'scheduled_payments',
        name: 'Scheduled Payments',
        subtitle: 'Auto-charge schedules created, cancelled and retried, counted from the history log — not live totals.',
        noun: 'Scheduled payments',
        actions: [
            created('PAYMENT_SCHEDULE_CREATED', 'Schedule created', 'PAYMENT_SCHEDULE', PALETTE[0]),
            updated('PAYMENT_SCHEDULE_UPDATED', 'Schedule edited', 'PAYMENT_SCHEDULE', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    subscriptions: {
        id: 'subscriptions',
        name: 'Subscriptions',
        subtitle: 'Subscriptions started, changed and cancelled, counted from the history log — not live totals.',
        noun: 'Subscriptions',
        actions: [
            created('SUBSCRIPTION_CREATED', 'Subscription created', 'SUBSCRIPTION', PALETTE[0]),
            updated('SUBSCRIPTION_UPDATED', 'Subscription edited', 'SUBSCRIPTION', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    insurance: {
        id: 'insurance',
        name: 'Insurance',
        subtitle: 'Driver insurance statuses and removals, counted from the history log — not live totals.',
        // "Insurance" names the primary record this report is about --
        // DRIVER_INSURANCE_STATUS -- the same relationship `incidents` has to
        // its own INCIDENT entity. DRIVER_REMOVED_REASON is a related but
        // distinct record (why a driver's insurance was removed, not the
        // insurance status itself), so it is excluded from the headline
        // tiles below for the same reason incidents excludes parties and
        // documents: "Insurance created" must not silently include a removal
        // reason. The actions are already named "Insurance created"/"Insurance
        // edited" vs "Insurance Removal recorded"/"Insurance Removal edited",
        // which is exactly this split. Removal activity still counts toward
        // "Total edit events", same as every other action in the report.
        noun: 'Insurance',
        headlineActions: ['DRIVER_INSURANCE_STATUS_CREATED', 'DRIVER_INSURANCE_STATUS_UPDATED'],
        actions: [
            created('DRIVER_INSURANCE_STATUS_CREATED', 'Insurance created', 'DRIVER_INSURANCE_STATUS', PALETTE[0]),
            updated('DRIVER_INSURANCE_STATUS_UPDATED', 'Insurance edited', 'DRIVER_INSURANCE_STATUS', PALETTE[1]),
            created('DRIVER_REMOVED_REASON_CREATED', 'Insurance Removal recorded', 'DRIVER_REMOVED_REASON', PALETTE[2]),
            updated('DRIVER_REMOVED_REASON_UPDATED', 'Insurance Removal edited', 'DRIVER_REMOVED_REASON', PALETTE[3]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    boards: {
        id: 'boards',
        name: 'Boards',
        subtitle: 'Task board activity: tasks opened, edited and discussed, counted from the history log — not live totals.',
        // Boards themselves are never tracked (task_rabbit.Board has no
        // creator/editor-visible-as-history field the writer fires on -- see
        // activity/registry.py's comment on BOARD_ACTIONS) -- this report is
        // about the work done ON a board: tasks and the comments on them. So
        // the headline tiles pair with "Tasks", scoped to just the task
        // actions -- a comment is a different, subordinate record (discussion
        // on a task, not a task itself), the same relationship `incidents`
        // has to its documents and `insurance` has to its removal reasons.
        noun: 'Tasks',
        headlineActions: ['TASK_CREATED', 'TASK_UPDATED'],
        // task_rabbit.Board has no branch, tenant or org link of any kind --
        // boards belong to users and groups, not branches -- so this report
        // cannot be branch-attributed. Mirrors
        // activity/registry.py's ReportSpec.has_branch_attribution exactly,
        // rather than being inferred from `charts` lacking a by-branch entry:
        // an inferred flag could not tell "deliberately unattributed" apart
        // from "someone forgot the chart." Read by `availableKinds` in
        // ReportsPanel.js (no Branch filter offered) and by
        // standardChartsFor above (no by-branch chart).
        hasBranchAttribution: false,
        // ...but every task and comment does belong to exactly one board, so
        // this is the one report (besides `inbox`) that trades branch for a
        // second grouping dimension -- mirrors
        // `activity/registry.py`'s `ReportSpec.scope_label` exactly. Read by
        // `scopeLabelFor` below (no Board filter/chart on any other report)
        // and by `standardChartsFor` (the "By board" chart, in the by-branch
        // chart's own slot since there is no branch chart here).
        scopeLabel: 'Board',
        // Enables the By Board tab (see scopeTabFor). Boards only for now;
        // inbox can opt in with the same two keys.
        scopeTab: true,
        scopePlural: 'Boards',
        actions: [
            created('TASK_CREATED', 'Task created', 'TASK', PALETTE[0]),
            updated('TASK_UPDATED', 'Task edited', 'TASK', PALETTE[1]),
            created('TASK_COMMENT_CREATED', 'Comment added', 'TASK_COMMENT', PALETTE[2]),
            updated('TASK_COMMENT_UPDATED', 'Comment edited', 'TASK_COMMENT', PALETTE[3]),
        ],
        charts: standardChartsFor({ includeBranchChart: false, scopeChartLabel: 'Board' }),
        userCharts: USER_CHARTS,
    },
    inbox: {
        id: 'inbox',
        name: 'Inbox',
        subtitle: 'Messages sent and received, with their attachments, across every channel, counted from the history log — not live totals.',
        // An attachment is a file on a message, not a message of its own --
        // "Messages created"/"edited" must not silently include an
        // attachment upload, the same reasoning `incidents` applies to its
        // own documents.
        noun: 'Messages',
        headlineActions: ['MESSAGE_CREATED', 'MESSAGE_UPDATED'],
        // Branch attribution stays on (a message's driver still resolves to a
        // branch) *and* every message belongs to an inbox, so this report
        // carries both a branch chart/filter and a scope (Inbox)
        // chart/filter -- the only report that does. Mirrors
        // `activity/registry.py`'s `ReportSpec.scope_label` exactly.
        scopeLabel: 'Inbox',
        // Pinned against `ReportSpec.has_direction` by
        // `activity/tests/test_frontend_contract.py`. The only report whose rows
        // carry a `direction`; every other report's rows have `null`.
        hasDirection: true,
        // Which entity the in/out tile counts. Declared here rather than
        // hardcoded in the panel so the tile's "messages, not attachments"
        // scope lives beside the actions it refers to.
        directionEntity: 'MESSAGE',
        actions: [
            created('MESSAGE_CREATED', 'Message created', 'MESSAGE', PALETTE[0]),
            updated('MESSAGE_UPDATED', 'Message edited', 'MESSAGE', PALETTE[1]),
            created('MESSAGE_ATTACHMENT_UPLOADED', 'Attachment uploaded', 'MESSAGE_ATTACHMENT', PALETTE[2]),
            updated('MESSAGE_ATTACHMENT_UPDATED', 'Attachment edited', 'MESSAGE_ATTACHMENT', PALETTE[3]),
        ],
        charts: standardChartsFor({ includeBranchChart: true, scopeChartLabel: 'Inbox', colorStaffByDirection: true }),
        userCharts: USER_CHARTS,
    },
    leads: {
        id: 'leads',
        name: 'Leads',
        subtitle: 'Customer leads opened and worked, counted from the history log — not live totals.',
        noun: 'Leads',
        actions: [
            created('LEAD_CREATED', 'Lead opened', 'LEAD', PALETTE[0]),
            updated('LEAD_UPDATED', 'Lead edited', 'LEAD', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    pricing: {
        id: 'pricing',
        name: 'Pricing',
        subtitle: 'Pricing and promotion templates created and adjusted, counted from the history log — not live totals.',
        // Both template types are equally "templates" -- like `claims`'
        // liability/subrogation pair -- so every action stays headline; no
        // `headlineActions` override needed.
        noun: 'Templates',
        actions: [
            created('PRICING_TEMPLATE_CREATED', 'Pricing template created', 'PRICING_TEMPLATE', PALETTE[0]),
            updated('PRICING_TEMPLATE_UPDATED', 'Pricing template edited', 'PRICING_TEMPLATE', PALETTE[1]),
            created('PROMOTION_TEMPLATE_CREATED', 'Promotion template created', 'PROMOTION_TEMPLATE', PALETTE[2]),
            updated('PROMOTION_TEMPLATE_UPDATED', 'Promotion template edited', 'PROMOTION_TEMPLATE', PALETTE[3]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    maintenance: {
        id: 'maintenance',
        name: 'Maintenance',
        subtitle: 'Car issues reported and worked, counted from the history log — not live totals.',
        noun: 'Car issues',
        actions: [
            created('CAR_ISSUE_CREATED', 'Issue reported', 'CAR_ISSUE', PALETTE[0]),
            updated('CAR_ISSUE_UPDATED', 'Issue edited', 'CAR_ISSUE', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
}

/**
 * The chart layout for a viewer restricted to their own rows: a trend chart
 * and a per-branch chart, in place of the report's full layout. Built from
 * the report's own `charts` -- reusing its actual trend chart and its actual
 * branchName-grouped `actionMix` chart -- rather than a hand-written parallel
 * list, so a report that scopes those charts down to a subset of its actions
 * (see `chart.actions`, and `incidents` in particular) keeps that scoping
 * here too. A hand-written list has no `actions` of its own, so
 * `seriesForChart` would fall back to the whole report's action list --
 * exactly the palette-collision and unit-mixing bug this replaces.
 *
 * The two people-comparing chart types are dropped outright, same as before:
 * `actionMix` grouped by `userName` ("by staff member") and the heatmap. A
 * one-bar chart and a one-row heatmap say nothing about one person's own
 * activity.
 */
export const selfScopedChartsFor = (report) => {
    const trendChart = report.charts.find((chart) => chart.type === 'trend')
    const branchChart = report.charts.find(
        (chart) => chart.type === 'actionMix' && chart.groupBy === 'branchName'
    )
    const charts = []
    if (trendChart) {
        charts.push({ ...trendChart, title: 'Your activity by {period}', caption: 'Your events per {period} in this range' })
    }
    if (branchChart) {
        charts.push({ ...branchChart, title: 'Your work by branch', caption: 'Created vs edited, this range' })
    }
    return charts
}

/**
 * The Users view for a viewer restricted to their own rows. `Leaderboard` and
 * the heatmap are both people-comparing charts -- a one-row leaderboard or
 * heatmap says nothing about one person's own activity, same reasoning as
 * `selfScopedChartsFor` above -- so only the trend chart survives, retitled
 * the same way.
 */
export const selfScopedUserChartsFor = (report) => {
    const trendChart = report.userCharts.find((chart) => chart.type === 'trend')
    if (!trendChart) return []
    return [{ ...trendChart, title: 'Your activity by {period}', caption: 'Your events per {period} in this range' }]
}

export const seriesFor = (report) =>
    report.actions.map((action) => ({
        key: action.value,
        label: action.label,
        color: action.color,
    }))

/**
 * Series for one chart. A chart marked `colorBy: 'direction'` (the `inbox`
 * by-staff chart -- see `standardChartsFor`'s `colorStaffByDirection` branch) is keyed
 * by the two fixed `DIRECTION_SERIES` entries rather than anything derived
 * from `report.actions`: direction is a property of the message a row acted
 * on, not an action, so there is no per-report variation to resolve here.
 *
 * A chart marked `colorBy: 'entity'` (the Users-view
 * trend -- see `USER_CHARTS`) is keyed by entity, not action, so it shares
 * its colour axis with the leaderboard on the same tab: one slot per
 * distinct entity, from `entityPaletteFor`, the exact derivation
 * `buildLeaderboard` uses via `entityColorFor`. Created and updated collapse
 * into the same line there -- that chart already scopes to one series per
 * entity, not per action.
 *
 * A chart marked `colorBy: 'entityPerAction'` (the incidents daily trend --
 * see its entry above) is different: it still needs *one line per action*
 * (created and updated must stay two separate series, or an entity's two
 * counts get summed into one line), but colouring all ten of `incidents`'
 * actions flat would need ten palette slots for a chart with only eight. So
 * colour is taken from the entity palette (one hue per entity, matching the
 * leaderboard) and every series also carries `dashed: true` for its
 * `updated`-mode actions -- `TrendLine` renders that as a dashed line, so
 * created vs updated is told apart by line style instead of a second colour.
 * Five entities x two styles identifies all ten actions within an eight-slot
 * palette.
 *
 * A chart that declares `actions` without a `colorBy` is scoped to just
 * those -- one series per action, in the order given, each keeping its own
 * flat `action.color` -- which lets a chart scope itself down to a handful
 * of actions (e.g. `incidents`' per-entity charts) without mixing record
 * types under one legend. A chart with neither covers the whole report and
 * falls back to `seriesFor`, which is how every report's unscoped charts
 * keep today's behaviour.
 */
export const seriesForChart = (report, chart) => {
    if (chart.colorBy === 'direction') return DIRECTION_SERIES
    if (chart.colorBy === 'entity') {
        return entityPaletteFor(report).map(({ entity, color }) => ({
            key: entity,
            label: entityLabel(entity),
            color,
        }))
    }
    if (chart.colorBy === 'entityPerAction') {
        const entityColor = entityColorFor(report)
        const actionValues = chart.actions || report.actions.map((action) => action.value)
        return actionValues.map((actionValue) => {
            const action = report.actions.find((candidate) => candidate.value === actionValue)
            return {
                key: action.value,
                label: action.label,
                color: entityColor(action.entity),
                dashed: action.mode === 'updated',
            }
        })
    }
    return chart.actions
        ? chart.actions.map((actionValue) => {
            const action = report.actions.find((candidate) => candidate.value === actionValue)
            return { key: action.value, label: action.label, color: action.color }
        })
        : seriesFor(report)
}

/**
 * Entity -> palette colour for a report's leaderboard: one slot per distinct
 * entity among the report's own actions, assigned in the order those
 * entities first appear in `report.actions`.
 *
 * This is a different colour axis from `action.color` above. `action.color`
 * distinguishes created vs updated *within* one entity (what
 * `seriesFor`/`seriesForChart` hand out, e.g. `INCIDENT_CREATED` and
 * `INCIDENT_UPDATED` get different slots), which is right for a chart legend
 * keyed by action. The leaderboard is keyed by entity instead -- it stacks
 * `created_<ENTITY>` and `updated_<ENTITY>` together under one bar per user
 * -- so it needs one colour per *entity*, not one per action. Colouring it
 * per action (what this replaces) can give a report more colour-bearing
 * entities than the palette has actions-per-entity slots for: `incidents`
 * has 10 actions across only 5 entities, so two entities ended up sharing a
 * slot even though the palette has room for all five.
 *
 * Derived from `report.actions` rather than a separate hand-maintained
 * entity table, so a report's entity list can never drift out of sync with
 * the actions that define it -- add or remove an action and the leaderboard
 * palette follows automatically.
 */
export const entityPaletteFor = (report) => {
    const entities = []
    report.actions.forEach((action) => {
        if (!entities.includes(action.entity)) entities.push(action.entity)
    })
    return entities.map((entity, index) => ({ entity, color: PALETTE[index % PALETTE.length] }))
}

/** Lookup version of `entityPaletteFor`, for `buildLeaderboard`. */
export const entityColorFor = (report) => {
    const byEntity = entityPaletteFor(report).reduce(
        (acc, { entity, color }) => ({ ...acc, [entity]: color }),
        {}
    )
    return (entityValue) => byEntity[entityValue]
}

export const getReport = (reportId) => REPORTS[reportId]

/**
 * The action values that scope a report's "created"/"edited" stat tiles --
 * `report.headlineActions` when the registry declares one (see `incidents`
 * and `insurance` above), or every one of the report's own actions
 * otherwise. That default is what lets the eight single-record-type reports
 * skip declaring `headlineActions` at all: their whole action list already
 * *is* the headline.
 */
export const headlineActionsFor = (report) => report.headlineActions || report.actions.map((action) => action.value)

/**
 * Tiles a report declares beyond the five generic ones every report shows,
 * rendered in the stat row right after them -- e.g. incidents' "Documents
 * uploaded" above. Each entry is `{ key, label, caption, actions, testId }`;
 * `actions` scopes the tile to a subset of the report's own actions, the same
 * shape `chart.actions` already uses elsewhere in this file. Registry-driven
 * so a report opts in by declaring `extraTiles` -- there is no `report.id`
 * branch in `ReportBody.js` deciding this per report -- and the default here
 * (no tiles) is what leaves the other eight reports' stat row untouched.
 */
export const extraTilesFor = (report) => report.extraTiles || []

/**
 * Whether a report's rows can be attributed to a branch at all -- mirrors
 * `activity.registry.ReportSpec.has_branch_attribution` (see the `boards`
 * entry above, the one report that sets this `false`). Defaults to `true`
 * by omission, same as the backend dataclass field, so the other thirteen
 * reports need not declare it. `ReportsPanel.js` reads this to decide
 * whether Branch belongs in `availableKinds` -- there is no `report.id`
 * branch anywhere for this, here or there.
 */
export const hasBranchAttributionFor = (report) => report.hasBranchAttribution !== false

/**
 * A report's one optional additional grouping dimension label -- mirrors
 * `activity.registry.ReportSpec.scope_label` (see the `boards` and `inbox`
 * entries above, the only two reports that set this). `null` by omission,
 * same as the backend dataclass field, so the other twelve reports need not
 * declare it. `ReportsPanel.js` reads this to decide whether the scope kind
 * belongs in `availableKinds` and what to label it; `ReportToolbar.js` reads
 * the label it resolves to. There is no `report.id` branch anywhere for
 * this, here or there.
 */
export const scopeLabelFor = (report) => report.scopeLabel || null

/** The lookup every function in `activityReport.js` expects. */
export const actionMetaFor = (report) => {
    const byValue = (report?.actions || []).reduce(
        (acc, action) => ({ ...acc, [action.value]: action }),
        {}
    )
    return (actionValue) => byValue[actionValue]
}
